import ArrowBackIcon from "@mui/icons-material/ArrowBackRounded";
import { Button, Dialog, DialogContent, DialogTitle, IconButton, Stack } from "@mui/material";
import deepEqual from "fast-deep-equal";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { ClientUserGroupInfo } from "../../../../api/types/userManagementTypes";
import type {
  ApprovalConfigurationVersion,
  ApprovalGroup,
  ApprovalStep,
  CreateApprovalConfigurationVersionRequest,
} from "../../../../api/types/workflowTypes";
import { ApprovalConfigurationContextProvider } from "./ApprovalConfigurationContext";
import type { ApprovalConfigurationState } from "./approvalConfigurationState";
import {
  cancelEditingStepAction,
  getCreateApprovalConfigurationVersionRequest,
  validateApprovalStep,
} from "./approvalConfigurationState";
import ApprovalStepsSection from "./ApprovalStepsSection";
import EditApprovalStepSection from "./EditApprovalStepSection";
import useApprovalRuleOptions from "./useApprovalRuleOptions";

const createApprovalConfigurationVersion = withErrorHandling(adminApi.createApprovalConfigurationVersion);

// Rule cards look up group names in the configuration, which only lists groups of saved steps.
const addReferencedGroups = (
  groups: ApprovalGroup[],
  steps: ApprovalStep[],
  clientUserGroups: ClientUserGroupInfo[]
): ApprovalGroup[] => {
  const referencedGroupIds = new Set(steps.flatMap((step) => step.rules.flatMap((rule) => rule.groupIds)));
  const missingGroups = clientUserGroups
    .filter((group) => referencedGroupIds.has(group.id) && !groups.some((known) => known.id === group.id))
    .map((group) => ({ id: group.id, name: group.name, users: [] }));
  return [...groups, ...missingGroups];
};

interface Props {
  approvalConfiguration: ApprovalConfigurationVersion;
  onClose: () => void;
  onSaved: (approvalConfiguration: ApprovalConfigurationVersion) => void;
}

const ApprovalConfigurationDialog = ({ approvalConfiguration, onClose, onSaved }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [draft, setDraft] = useState(approvalConfiguration);
  const [state, setState] = useState<ApprovalConfigurationState>({});
  const [saving, setSaving] = useState(false);

  const {
    clientUserGroups,
    userGroupsError,
    isFetchingUserGroups,
    lookupOptionsByObjectType,
    lookupOptionsUnavailable,
  } = useApprovalRuleOptions(draft, state.editedStep, true);

  const saveDraft = (request: CreateApprovalConfigurationVersionRequest) => {
    setDraft((prev) => ({
      ...prev,
      ...request,
      groups: addReferencedGroups(prev.groups, request.steps, clientUserGroups),
    }));
    return Promise.resolve(true);
  };

  const handleApplyStepClick = () => {
    const request = getCreateApprovalConfigurationVersionRequest(state, draft);
    if (request === undefined) {
      return;
    }

    void saveDraft(request);
    setState({});
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  const handleSaveClick = async () => {
    setSaving(true);
    const [resp, error] = await createApprovalConfigurationVersion(
      draft.workflowTemplateVersionId,
      draft.workflowActivityId,
      {
        steps: draft.steps,
        conversionTargetCurrencyCode: draft.conversionTargetCurrencyCode,
        approversAssignmentMode: "Automatic",
      }
    );
    setSaving(false);

    if (error) {
      logError(error, "[ApprovalConfigurationDialog] createApprovalConfigurationVersion");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Changes saved successfully");
    onSaved(resp);
  };

  if (userGroupsError) {
    logError(userGroupsError, "[ApprovalConfigurationDialog] getClientUserGroups");
  }

  const editedStepValidationResults = validateApprovalStep(state, draft.steps);
  const isDirty = !deepEqual(draft.steps, approvalConfiguration.steps);

  return (
    <Dialog open fullWidth maxWidth="md" slotProps={{ paper: { sx: { height: "50vh" } } }}>
      <DialogTitle component="div">
        {state.editedStep === undefined ? (
          draft.workflowName
        ) : (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <IconButton sx={{ my: -0.75 }} onClick={() => setState(cancelEditingStepAction())}>
              <ArrowBackIcon />
            </IconButton>
            <span>Approval Step</span>
          </Stack>
        )}
      </DialogTitle>
      <DialogCloseButton onClick={handleClose} />
      <DialogContent dividers>
        {userGroupsError && <DataLoadingFailed />}
        {!userGroupsError && isFetchingUserGroups && <InlineLoader />}
        {!userGroupsError && !isFetchingUserGroups && (
          <ApprovalConfigurationContextProvider
            hasEditPermissions
            isDraft
            approvalConfiguration={draft}
            state={state}
            setState={setState}
            editedStepValidationResults={editedStepValidationResults}
            clientUserGroups={clientUserGroups}
            lookupOptionsByObjectType={lookupOptionsByObjectType}
            lookupOptionsUnavailable={lookupOptionsUnavailable}
            saveConfiguration={saveDraft}
          >
            {state.editedStep === undefined ? <ApprovalStepsSection /> : <EditApprovalStepSection />}
          </ApprovalConfigurationContextProvider>
        )}
      </DialogContent>
      {state.editedStep === undefined ? (
        <DialogButtons onCancel={handleClose} cancelDisabled={saving}>
          <Button variant="contained" disabled={!isDirty} loading={saving} onClick={handleSaveClick}>
            Save
          </Button>
        </DialogButtons>
      ) : (
        <DialogButtons onCancel={() => setState(cancelEditingStepAction())}>
          <Button
            variant="contained"
            disabled={!state.isEditedStepDirty || !editedStepValidationResults?.isValid}
            onClick={handleApplyStepClick}
          >
            Apply
          </Button>
        </DialogButtons>
      )}
    </Dialog>
  );
};

export default ApprovalConfigurationDialog;
